import merge from 'lodash.merge';
import get from 'lodash.get';

import { isExpired, DAY } from 'lib/expirationHelpers';
import SmoochSessionStorage from 'adapters/gateways/SmoochSessionStorage';
import CohortGateway from 'adapters/gateways/CohortGateway';
import { updateInitialStateFromCookies } from 'middleware/cookiesMiddleware';

export default class LocalStorageGateway {
  constructor(appId, readFromDeprecatedStorage = false) {
    this.appId = appId;
    this.hasMutated = false;
    this.readFromDeprecatedStorage = readFromDeprecatedStorage;
    this.deprecatedSessionStorage = new SmoochSessionStorage();
    this.deprecatedCohortStorage = new CohortGateway();
  }

  save(key, value) {
    this.hasMutated = true;

    /*
     In a transition period we write to both old and new storage.
    */
    if (key == 'user') {
      this.deprecatedSessionStorage.patchSession({
        gladlyCustomerId: value.gladlyCustomerId,
        hasOnboarded: value.hasOnboarded,
        jwt: value.token,
        userId: value.userId,
      });
    }

    if (key == 'welcomeMessage') {
      this.deprecatedSessionStorage.patchSession({
        welcomeMessage: value,
      });
    }

    if (key == 'cohortId') {
      this.deprecatedCohortStorage.setCohortId(this.appId, value);
    }

    let rawValue = localStorage.getItem(this.appId);
    let storedValue;
    if (rawValue) {
      storedValue = JSON.parse(rawValue);
    } else {
      storedValue = {
        values: {},
      };
    }

    storedValue.updatedAt = Date.now();
    storedValue.values[key] = value;
    localStorage.setItem(this.appId, JSON.stringify(storedValue));
  }

  delete(key) {
    this.hasMutated = true;

    if (key == 'user') {
      this.deprecatedSessionStorage.removeSession();
    }
    localStorage.removeItem(this.appId);
  }

  getInitialState(key, path) {
    if (this.hasMutated) {
      throw "can't read state after it has been mutated.";
    }

    if (this.readFromDeprecatedStorage) {
      return this.getFromDeprecatedStorage(key, path);
    }

    const rawData = localStorage.getItem(this.appId);
    if (!rawData) {
      return null;
    }

    const data = JSON.parse(rawData);
    if (isExpired(data.updatedAt, DAY)) {
      this.delete(this.appId);
      return null;
    }

    return data.values[key];
  }

  /*
  In a transition period old storage overwrite new storage
  */
  getFromDeprecatedStorage(key, path) {
    if (key == 'user') {
      const storedSession = this.deprecatedSessionStorage.getSession();
      const cookieState = updateInitialStateFromCookies(this.appId, {});
      const cookieUser = get(cookieState, path);
      if (storedSession) {
        return merge({}, cookieUser, {
          gladlyCustomerId: storedSession.gladlyCustomerId,
          hasOnboarded: storedSession.hasOnboarded,
          token: storedSession.jwt,
          userId: storedSession.userId,
        });
      }
    }

    if (key == 'welcomeMessage') {
      const storedSession = this.deprecatedSessionStorage.getSession();
      if (storedSession) {
        return storedSession.welcomeMessage;
      }
    }

    if (key == 'cohortId') {
      return this.deprecatedCohortStorage.getCohortId(this.appId);
    }

    const cookieState = updateInitialStateFromCookies(this.appId, {});
    return get(cookieState, path);
  }
}
